import { normalizeRating } from "../../../utils/teacher/InputRatingUtils";
import { gradeTranslation } from "../../../utils/SchoolAdmin/translations";

const API_GRADE_ORDER = {
    K: 0,
    FIRST: 1,
    SECOND: 2,
    THIRD: 3,
    FOURTH: 4,
    FIFTH: 5,
    SIXTH: 6,
    SEVENTH: 7,
    EIGHTH: 8
};

export const cellKey = (studentKey, grade, contentId) => `${studentKey}|${grade}|${String(contentId)}`;

const candidateIdentity = (candidate) => {
    if (candidate?.studentNumber != null && String(candidate.studentNumber) !== "") {
        return String(candidate.studentNumber);
    }
    return `gps:${candidate.gradingProfileStudentId}`;
};

/**
 * Same kid in two selected groups is one row. Prefer EDIT over VIEW (on-level
 * RatingGrid does this by studentNumber). Missing studentNumber falls back to
 * gradingProfileStudentId so unrelated students are not collapsed.
 */
export const dedupeGroupCandidatesByStudentNumber = (candidates) => {
    const byId = new Map();
    (candidates || []).forEach((candidate) => {
        const id = candidateIdentity(candidate);
        if (!byId.has(id)) byId.set(id, []);
        byId.get(id).push(candidate);
    });

    const result = [];
    byId.forEach((instances) => {
        result.push(instances.find((s) => s.canEdit) || instances[0]);
    });
    return result;
};

export const gradeTabLabel = (grade) => {
    const display = gradeTranslation[grade] || grade;
    if (display === "K") return "Kindergarten";
    return `${display} Grade`;
};

export const sortApiGrades = (grades) =>
    [...new Set(grades.filter(Boolean))].sort((a, b) => (API_GRADE_ORDER[a] ?? 99) - (API_GRADE_ORDER[b] ?? 99));

export const itemColumnKey = (grade, contentId) => `${grade}|${String(contentId)}`;

/** Identity of the current diff roster. Used to reload and to flush on leave. */
export const buildDiffRosterKey = ({ mode, groupData, existingRatingsData }) => {
    if (mode === "GROUPS") {
        return (groupData || [])
            .map(
                (g) => `${g.groupId}:${(g.groupStudentContents || []).map((s) => s.gradingProfileStudentId).join(",")}`
            )
            .join("|");
    }
    return (existingRatingsData || [])
        .filter((e) => e.differentiated === true)
        .map((e) => e.key)
        .join(",");
};

/**
 * Homeroom: only students with differentiated: true on the roster GET.
 */
export const collectHomeroomCandidates = ({ existingRatingsData, selectedEpr, selectedTemplate }) => {
    const criteria = selectedEpr?.gradingCriteriaList?.find(
        (c) => Number.parseInt(c.parentTemplate, 10) === Number(selectedTemplate?.templateId)
    );
    if (!criteria) return [];

    const rosterById = new Map((selectedEpr?.gradingProfileStudents || []).map((s) => [String(s.key), s]));

    return (existingRatingsData || [])
        .filter((entry) => entry.differentiated === true)
        .map((entry) => {
            const roster = rosterById.get(String(entry.key)) || {};
            return {
                studentKey: String(entry.key),
                gradingProfileStudentId: Number(entry.key),
                gradingCriteriaId: Number(criteria.key),
                firstName: roster.firstName || "",
                lastName: roster.lastName || "",
                studentNumber: entry.studentNumber || roster.studentNumber,
                canEdit: true
            };
        });
};

/**
 * Groups: no differentiated flag — caller drops empty diffRatings after fetch.
 * Own groups use the teacher's criteria; shared groups use sharedGradingCriteria.
 * Same studentNumber across selected groups is one candidate; EDIT wins over VIEW.
 */
export const collectGroupCandidates = ({ groupData, selectedEpr, selectedTemplate }) => {
    const ownCriteria = selectedEpr?.gradingCriteriaList?.find(
        (c) => Number.parseInt(c.parentTemplate, 10) === Number(selectedTemplate?.templateId)
    );

    const flat = (groupData || []).flatMap((group) => {
        const gradingCriteriaId = group.shareGroupId ? Number(group.sharedGradingCriteria) : Number(ownCriteria?.key);
        if (!gradingCriteriaId) return [];

        const canEdit = group.permission !== "VIEW";
        return (group.groupStudentContents || []).map((s) => ({
            studentKey: `${group.groupId}-${s.gradingProfileStudentId}`,
            gradingProfileStudentId: Number(s.gradingProfileStudentId),
            gradingCriteriaId,
            firstName: s.firstName || "",
            lastName: s.lastName || "",
            studentNumber: s.studentNumber,
            groupName: group.groupName,
            groupId: group.groupId,
            groupHomeroomTeacher: group.teacherName,
            isShared: Boolean(group.shareGroupId),
            groupPermission: group.permission,
            gradingProfileStatus: group.gradingProfileStatus,
            canEdit
        }));
    });

    return dedupeGroupCandidatesByStudentNumber(flat);
};

/**
 * Index getStudentRatings payloads into grade tabs, items, and cell ratings.
 * Students with no diffRatings are dropped (Groups path).
 */
export const indexDiffPayloads = (rows) => {
    const grades = [];
    const itemsByGrade = {};
    const studentsByGrade = {};
    const ratings = {};
    const comments = {};
    const snapshots = {};
    const students = [];

    (rows || []).forEach((row) => {
        const diffRatings = row.payload?.diffRatings || [];
        if (!diffRatings.length) return;

        students.push(row.candidate);
        snapshots[row.candidate.studentKey] = {
            contentRatings: row.payload.contentRatings || [],
            diffRatings,
            gradingProfileStudentId: row.candidate.gradingProfileStudentId,
            gradingCriteriaId: row.candidate.gradingCriteriaId
        };

        diffRatings.forEach((dr) => {
            if (!dr.grade) return;
            const contentId = String(dr.contentId);
            grades.push(dr.grade);
            if (!itemsByGrade[dr.grade]) itemsByGrade[dr.grade] = new Map();
            const colKey = itemColumnKey(dr.grade, contentId);
            if (!itemsByGrade[dr.grade].has(colKey)) {
                itemsByGrade[dr.grade].set(colKey, {
                    grade: dr.grade,
                    contentId: dr.contentId,
                    contentType: dr.contentType,
                    gradeToContentId: dr.gradeToContentId,
                    label: shortItemLabel(dr)
                });
            }
            if (!studentsByGrade[dr.grade]) studentsByGrade[dr.grade] = new Set();
            studentsByGrade[dr.grade].add(row.candidate.studentKey);

            const displayRating = dr.rating === "UNRATED" || !dr.rating ? "" : normalizeRating(dr.rating);
            const key = cellKey(row.candidate.studentKey, dr.grade, contentId);
            ratings[key] = displayRating;
            comments[key] = dr.comment || "";
        });
    });

    const items = {};
    Object.keys(itemsByGrade).forEach((grade) => {
        items[grade] = [...itemsByGrade[grade].values()];
    });
    const studentsAtGrade = {};
    Object.keys(studentsByGrade).forEach((grade) => {
        studentsAtGrade[grade] = [...studentsByGrade[grade]];
    });

    return {
        grades: sortApiGrades(grades),
        itemsByGrade: items,
        studentsByGrade: studentsAtGrade,
        ratings,
        comments,
        snapshots,
        students
    };
};

const CONTENT_TYPE_SHORT = {
    EVIDENCE_OUTCOME_CHILD: "EOC",
    EVIDENCE_OUTCOME: "EO",
    GLE: "GLE"
};

export const shortItemLabel = (dr) => {
    const type = CONTENT_TYPE_SHORT[dr.contentType] || dr.contentType || "Item";
    return `${type} ${dr.contentId}`;
};

export const studentHasItem = (snapshot, grade, contentId) =>
    (snapshot?.diffRatings || []).some((dr) => dr.grade === grade && String(dr.contentId) === String(contentId));

export const findDiffRating = (snapshot, grade, contentId) =>
    (snapshot?.diffRatings || []).find((dr) => dr.grade === grade && String(dr.contentId) === String(contentId));
